// Khung "Glass" cho điện thoại (data-viewport="mobile"): nội dung nổi trên nền cực
// quang nhuộm theo cấp (Shop xanh, CTV tím, Đại lý hổ phách). Thanh trên bằng kính
// có ảnh + tên đơn vị (chỉ để xem, không bấm), nút Giới thiệu (Shop / CTV Bán hàng) và chuông; dock kính nổi ở đáy là một
// dải tab vuốt ngang + ô Cài đặt ghim cuối (30/10/2026 — Cài đặt gom cả menu tài khoản
// cũ, xem components/layout/PhoneSettingsHub.tsx), nút Tạo đơn tròn nổi trên mép phải.
// Khu Quản trị NPP giữ 4 ô + "Thêm" mở cả menu NPP, cuối bảng là phần tài khoản. Cùng
// menu, quyền, hành động với khung gốc (lấy từ shell-model) — chỉ khác hình. CSS: src/styles/glass.css.

import { useEffect, useRef, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Plus, LogOut, ChevronRight, ArrowUpRight, ArrowLeftRight, Hand, LayoutGrid, UserPlus, ScanLine, QrCode } from 'lucide-react'
import { InstallAppCard } from '@/components/InstallAppCard'
import { NotificationBell } from '@/components/NotificationBell'
import { NppNotificationBell } from '@/components/announce/NppNotificationBell'
import { cn } from '@/lib/utils'
import { ShellContext, useShellModel, type PhoneMoreEntry } from './shell-model'
import { UnitAvatar } from '@/components/settings/ShopAvatar'
import { ShopReferralDialog } from '@/components/settings/ShopReferralCard'
import { PoweredBy, ShopTrackLogo } from '@/components/ui/logo'
import { useBrand } from '@/lib/brand'
import { NavList } from './DashboardLayout'
import { setWorkMode } from '@/lib/workMode'
import { ModeChooser } from './ModeSwitch'
import { useModeSlide } from './useModeSlide'
import { PricingBanner, isUnitType } from '@/components/pricing/PricingNotice'
import { SeasonDeco, SeasonDockCap, SeasonFlag, SeasonHat, SeasonLayer } from '@/components/season/Season'

export default function GlassLayout() {
  const shell = useShellModel()
  const {
    shopName, levelInfo, roleTheme, phoneTabs, accountMenu, primaryAction, addUnitTo, avatarUrl,
    activePhone, onLogout, dock, settingsTab, activeDock,
    canSwitchMode, mode, modeChosen, switchMode, tenantType,
    nppMode, crossLink, currentNav, activePath, currentLabel, canPeerRefer, referLabel, referKind,
  } = shell
  const brand = useBrand()
  const slideRef = useModeSlide<HTMLElement>(mode)

  // Khu Quản trị NPP: "Thêm" mở cả menu NPP (~16 mục) + phần tài khoản
  const [nppMenuOpen, setNppMenuOpen] = useState(false)
  const closeNpp = () => setNppMenuOpen(false)
  // Shop / CTV Bán hàng: mã QR giới thiệu to (nút cạnh chuông ở thanh trên)
  const [referOpen, setReferOpen] = useState(false)
  const location = useLocation()
  // Đổi trang (chọn mục, nút Back) → menu đóng
  useEffect(() => {
    setNppMenuOpen(false)
  }, [location.pathname, location.search])

  return (
    <ShellContext.Provider value={shell}>
    <div data-role-theme={roleTheme} className="gx-app">
      <div className="gx-aurora" aria-hidden />
      <SeasonLayer />

      <header className="gx-top">
        {/* Ảnh + tên đơn vị (khu NPP: tên NPP) — chỉ để biết đang ở đâu, bấm không làm gì. Mọi thứ của menu tài khoản cũ
            nằm ở tab Cài đặt (khu NPP: cuối bảng "Thêm"). */}
        <div className="gx-who">
          <UnitAvatar name={shopName || 'S'} src={avatarUrl} className="gx-avatar">
            <SeasonHat />
          </UnitAvatar>
          <span className="gx-who-name">{shopName || levelInfo.levelTag}</span>
        </div>
        <span className="flex-1" />
        {/* Không đặt logo NPP ở thanh trên (31/10/2026, user): logo NPP chỉ ở cuối Cài đặt / menu Thêm của khu NPP */}
        <SeasonFlag />
        {/* Tạo đơn bằng Excel nằm trong trang Tạo đơn (routes/OrderCreate.tsx) */}
        {/* Chủ Shop / CTV ở Bán hàng: Giới thiệu cạnh chuông — bấm hiện mã QR to để người bên cạnh quét đăng ký */}
        {canPeerRefer && (
          <button type="button" className="gx-refer" onClick={() => setReferOpen(true)} aria-haspopup="dialog" aria-label={referLabel} title={referLabel}>
            <QrCode className="size-[22px]" aria-hidden />
          </button>
        )}
        <div className="gx-bell">
          {nppMode ? <NppNotificationBell /> : <NotificationBell />}
        </div>
        <SeasonDeco variant="glass" />
      </header>

      <main ref={slideRef} className="app-content gx-content">
        {/* Đơn vị chưa có bảng giá / chiết khấu (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.5) */}
        <PricingBanner enabled={!nppMode && isUnitType(tenantType)} className="mb-3" />
        {/* Trang quản trị NPP phần lớn không tự có tiêu đề (máy tính ghi tên mục ở thanh trên) — thanh trên Glass chỉ có
            tên NPP nên ghi tên mục ở đây; trang nào tự có <h1> thì CSS ẩn dòng này (glass.css .gx-page-title). */}
        {nppMode && currentLabel && <h1 className="gx-page-title">{currentLabel}</h1>}
        <Outlet />
      </main>

      <nav className={cn('gx-dock', !nppMode && 'is-strip')} aria-label="Điều hướng chính">
        <SeasonDockCap />
        {nppMode ? (
          <>
            {phoneTabs.map((item) => {
              const isActive = activePhone === item.to
              return (
                <Link key={item.to} to={item.to} aria-current={isActive ? 'page' : undefined} className={cn('gx-tab', isActive && 'is-active')}>
                  <span className="gx-tab-icon">
                    <item.Icon className="size-[22px]" aria-hidden />
                  </span>
                  <span className="gx-tab-label">{item.label}</span>
                </Link>
              )
            })}
            <button
              type="button"
              onClick={() => setNppMenuOpen(true)}
              aria-expanded={nppMenuOpen}
              aria-current={activePhone === 'more' ? 'page' : undefined}
              className={cn('gx-tab', activePhone === 'more' && 'is-active')}
            >
              <span className="gx-tab-icon">
                <LayoutGrid className="size-[22px]" aria-hidden />
              </span>
              <span className="gx-tab-label">Thêm</span>
            </button>
          </>
        ) : (
          <>
            <DockStrip items={dock} active={activeDock} onSwitch={switchMode} />
            <Link
              to={settingsTab.to}
              aria-current={activeDock === settingsTab.to ? 'page' : undefined}
              className={cn('gx-tab gx-dock-pin', activeDock === settingsTab.to && 'is-active')}
            >
              <span className="gx-tab-icon">
                <settingsTab.Icon className="size-[22px]" aria-hidden />
              </span>
              <span className="gx-tab-label">{settingsTab.label}</span>
            </Link>
          </>
        )}

        {/* Tạo đơn luôn trong tầm tay như app (CreateOrderFAB): nổi trên mép phải dock.
            Chế độ Quản lý / khu NPP: cùng chỗ là Thêm tài khoản (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.1) */}
        {primaryAction === 'createOrder' && (
          <Link to="/orders/new" className="gx-create" aria-label="Tạo đơn" data-season-origin="glass">
            <Plus className="size-6" strokeWidth={2.4} aria-hidden />
            <span className="gx-create-label">Tạo đơn</span>
          </Link>
        )}
        {primaryAction === 'addUnit' && addUnitTo && (
          <Link to={addUnitTo} className="gx-create" aria-label="Thêm tài khoản" data-season-origin="glass">
            <UserPlus className="size-5" strokeWidth={2.2} aria-hidden />
            <span className="gx-create-label gx-create-label-2" aria-hidden>
              Thêm
              <br />
              tài khoản
            </span>
          </Link>
        )}
      </nav>

      {nppMode && (
        <DialogPrimitive.Root open={nppMenuOpen} onOpenChange={setNppMenuOpen}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="gx-scrim data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
            <DialogPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto rounded-t-2xl border-t border-rule bg-raise px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-overlay data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-rule" aria-hidden />
              <DialogPrimitive.Title className="px-2 pb-2 text-base font-semibold">Quản trị nhà phân phối</DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">Mọi mục của khu Quản trị NPP, tài khoản và đăng xuất</DialogPrimitive.Description>
              <NavList items={currentNav} activePath={activePath} onNavigate={closeNpp} dense />

              {/* Phần của menu tài khoản cũ (ô tên góc trái nay không bấm được) */}
              <div className="gx-menu-sep mt-2" />
              {crossLink && (
                <Link to={crossLink.to} onClick={closeNpp} className="gx-menu-row">
                  <ArrowLeftRight className="size-[18px] shrink-0" aria-hidden />
                  <span className="flex-1">{crossLink.label}</span>
                  <ChevronRight className="size-3.5 shrink-0 opacity-50" aria-hidden />
                </Link>
              )}
              {/* Menu NPP đã có "Tài khoản" thì không lặp */}
              {accountMenu.filter((item) => !currentNav.some((n) => n.to === item.to)).map((item) => (
                <Link key={item.to} to={item.to} onClick={closeNpp} className="gx-menu-row">
                  <item.Icon className="size-[18px] shrink-0" aria-hidden />
                  <span className="flex-1">{item.label}</span>
                  <ChevronRight className="size-3.5 shrink-0 opacity-50" aria-hidden />
                </Link>
              ))}
              {/* Đăng nhập máy khác bằng mã QR (docs/KE_HOACH_DANG_NHAP_QR.md) */}
              <Link to="/qr" onClick={closeNpp} className="gx-menu-row">
                <ScanLine className="size-[18px] shrink-0" aria-hidden />
                <span className="flex-1">Quét mã đăng nhập</span>
                <ChevronRight className="size-3.5 shrink-0 opacity-50" aria-hidden />
              </Link>
              <a href="/privacy" target="_blank" rel="noopener" onClick={closeNpp} className="gx-menu-row">
                <Hand className="size-[18px] shrink-0" aria-hidden />
                <span className="flex-1">Chính sách quyền riêng tư</span>
                <ArrowUpRight className="size-3.5 shrink-0 opacity-50" aria-hidden />
              </a>
              <div className="gx-install px-2">
                <InstallAppCard />
              </div>
              <button type="button" onClick={onLogout} className="gx-menu-row is-danger">
                <LogOut className="size-[18px] shrink-0" aria-hidden />
                <span className="flex-1 text-left">Đăng xuất</span>
              </button>
              {brand && (
                <div className="flex flex-col items-center gap-1 px-3 pb-1 pt-3">
                  <ShopTrackLogo place="bar" />
                  <PoweredBy />
                </div>
              )}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}

      {canPeerRefer && <ShopReferralDialog open={referOpen} onOpenChange={setReferOpen} title={referLabel} kind={referKind} />}

      <ModeChooser
        open={canSwitchMode && !modeChosen}
        isAgency={tenantType === 'agency'}
        onPick={switchMode}
        onDismiss={() => setWorkMode(mode, true)}
      />
    </div>
    </ShellContext.Provider>
  )
}

/**
 * Dải tab vuốt ngang của dock: hơn 4 ô thì ô thứ 5 ló một phần làm dấu còn nữa, mép còn nội dung mờ dần. Đổi trang mà ô
 * đang sáng nằm ngoài khung thì cuộn tới ô đó.
 */
function DockStrip({ items, active, onSwitch }: { items: PhoneMoreEntry[]; active: string | null; onSwitch: (m: 'manage' | 'sell') => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [edge, setEdge] = useState({ start: true, end: true })
  const measure = () => {
    const el = ref.current
    if (!el) return
    setEdge({ start: el.scrollLeft <= 2, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 })
  }
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const tab = active ? el.querySelector<HTMLElement>('[aria-current="page"]') : null
    if (tab && (tab.offsetLeft < el.scrollLeft || tab.offsetLeft + tab.offsetWidth > el.scrollLeft + el.clientWidth)) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      el.scrollTo({ left: tab.offsetLeft - (el.clientWidth - tab.offsetWidth) / 2, behavior: reduce ? 'auto' : 'smooth' })
    }
    measure()
  }, [active, items.length])
  useEffect(() => {
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])
  return (
    <div
      ref={ref}
      className="gx-dock-scroll"
      data-many={items.length > 4 || undefined}
      data-more-start={!edge.start || undefined}
      data-more-end={!edge.end || undefined}
      onScroll={measure}
    >
      {items.map((item) => {
        const body = (
          <>
            <span className="gx-tab-icon">
              <item.Icon className="size-[22px]" aria-hidden />
            </span>
            <span className="gx-tab-label">{item.label}</span>
          </>
        )
        if (item.kind === 'switch') {
          return (
            <button key={item.label} type="button" className="gx-tab" onClick={() => onSwitch(item.mode)}>
              {body}
            </button>
          )
        }
        const isActive = active === item.to
        return (
          <Link key={item.to} to={item.to} aria-current={isActive ? 'page' : undefined} className={cn('gx-tab', isActive && 'is-active')}>
            {body}
          </Link>
        )
      })}
    </div>
  )
}
